import {entry, objectFromEntries} from '@benchling/js-utils';
import {useMemo} from 'react';

import {ProcedureType} from 'graphql-client/__generated/graphqlGlobalTypes';
import useSimpleEvaluation from 'notebook/simple-tables/useSimpleEvaluation';
import {FullTableMap} from 'notebook/tables/excess/EvaluationConstants';
import {
  ExperimentalInputsComponentName,
  ExperimentalInputsContextProperties,
} from 'procedures/planning/ProcedurePlanTypes';
import {
  convertExperimentalInputsToTable,
  getDefaultConditions,
  FilteredTableView,
  getLockedMaterialInputReasons,
  getVaryingParameterIndices,
  PendingRemovalTableOptions,
} from 'procedures/planning/useInputTable';

import {ExperimentFlowchart, ExperimentMethod} from '../design-state/ExperimentalInputs';
import {getRenderedConditionIndicesForMethod} from './FactorVariationUtils';

/**
 * Defined available modes for filtering experimental input tables: all parameters or only varying.
 */
export enum ExperimentalInputsFilterMode {
  ALL = 'all',
  VARYING = 'varying',
}

const EXPERIMENTAL_INPUT_COMPONENT_NAMES: Array<ExperimentalInputsComponentName> = [
  ExperimentalInputsComponentName.MATERIAL_INPUTS,
  ExperimentalInputsComponentName.PARAMETERS,
  ExperimentalInputsComponentName.EQUIPMENT,
];

const EMPTY_FULL_TABLE_MAP: FullTableMap = {};

export interface UseFilteredInputTableDocArgs {
  /**
   * Provides experimental input state and the original, unfiltered table doc.
   */
  context: ExperimentalInputsContextProperties;
  selectedMethod: ExperimentMethod;
  templateFlowchart: ExperimentFlowchart;
  procedureType: ProcedureType;
  inputTableFilterMode: ExperimentalInputsFilterMode;
  isProcedureRunPlan: boolean;
  removedNodeIds?: ReadonlySet<string>;
}

interface UseFilteredInputTableDocResult {
  /**
   * Evaluated, filtered table documents for each component, or `null` if filtering is not active.
   */
  filteredTableViewByComponent: Record<ExperimentalInputsComponentName, FilteredTableView> | null;
  /**
   * Removal-aware doc and write-back indices, or `null` when no removals are staged.
   */
  pendingRemovalOptions: PendingRemovalTableOptions | null;
  /**
   * Count of all parameters in the table.
   */
  allCount: number;
  /**
   * Count of varying parameters in the table.
   */
  variesCount: number;
}

/**
 * Returns counts of all and varying parameters, and a filtered evaluated table doc when tables are filtered.
 *
 * When tables are unfiltered, returns counts and `filteredTableViewByComponent: null` - callers render the
 * existing unfiltered `context.simpleTableDoc` directly if tables are not filtered.
 */
export default function useFilteredInputTableDoc({
  context,
  selectedMethod,
  templateFlowchart,
  procedureType,
  inputTableFilterMode,
  isProcedureRunPlan,
  removedNodeIds,
}: UseFilteredInputTableDocArgs): UseFilteredInputTableDocResult {
  const experimentalInputs = context.draftInputs[selectedMethod.id];

  const conditionsOrDefault = useMemo(
    () =>
      context.conditions.length === 0
        ? getDefaultConditions(experimentalInputs.values.length, procedureType)
        : context.conditions,
    [context.conditions, experimentalInputs.values.length, procedureType]
  );
  const visibleConditionIndices = useMemo(
    () =>
      getRenderedConditionIndicesForMethod(
        conditionsOrDefault,
        selectedMethod.id,
        context.showArchivedConditions ?? false,
        removedNodeIds
      ),
    [conditionsOrDefault, context.showArchivedConditions, removedNodeIds, selectedMethod.id]
  );

  const varyingIndicesByComponent = useMemo(
    () =>
      objectFromEntries(
        EXPERIMENTAL_INPUT_COMPONENT_NAMES.map((componentName) =>
          entry(
            componentName,
            getVaryingParameterIndices(experimentalInputs, componentName, visibleConditionIndices)
          )
        )
      ) as Record<ExperimentalInputsComponentName, Array<number>>,
    [experimentalInputs, visibleConditionIndices]
  );

  const allCount = useMemo(
    () =>
      EXPERIMENTAL_INPUT_COMPONENT_NAMES.reduce(
        (count, componentName) => count + experimentalInputs.parameterNames[componentName].length,
        0
      ),
    [experimentalInputs]
  );

  const variesCount = useMemo(
    () =>
      EXPERIMENTAL_INPUT_COMPONENT_NAMES.reduce(
        (count, componentName) => count + varyingIndicesByComponent[componentName].length,
        0
      ),
    [varyingIndicesByComponent]
  );

  const shouldFilterToVarying =
    inputTableFilterMode === ExperimentalInputsFilterMode.VARYING && variesCount > 0;

  const displayFullTableMap = useMemo(() => {
    if (removedNodeIds == null || removedNodeIds.size === 0) {
      return EMPTY_FULL_TABLE_MAP;
    }

    const lockedReasonByInputId = getLockedMaterialInputReasons(
      selectedMethod,
      templateFlowchart,
      procedureType
    );

    return objectFromEntries(
      EXPERIMENTAL_INPUT_COMPONENT_NAMES.flatMap((componentName) => {
        const table = convertExperimentalInputsToTable(
          experimentalInputs,
          componentName,
          lockedReasonByInputId,
          context.conditions,
          procedureType,
          selectedMethod,
          context.locked,
          isProcedureRunPlan,
          undefined,
          undefined,
          context.showArchivedConditions,
          visibleConditionIndices,
          removedNodeIds
        );
        return table ? [entry(`${selectedMethod.id}_${componentName}`, table)] : [];
      })
    );
  }, [
    context.conditions,
    context.locked,
    context.showArchivedConditions,
    experimentalInputs,
    isProcedureRunPlan,
    procedureType,
    removedNodeIds,
    selectedMethod,
    templateFlowchart,
    visibleConditionIndices,
  ]);

  const displaySimpleTableDoc = useSimpleEvaluation(displayFullTableMap);

  const filteredFullTableMap = useMemo(() => {
    if (!shouldFilterToVarying) {
      return EMPTY_FULL_TABLE_MAP;
    }

    const lockedReasonByInputId = getLockedMaterialInputReasons(
      selectedMethod,
      templateFlowchart,
      procedureType
    );

    return objectFromEntries(
      EXPERIMENTAL_INPUT_COMPONENT_NAMES.flatMap((componentName) => {
        const table = convertExperimentalInputsToTable(
          experimentalInputs,
          componentName,
          lockedReasonByInputId,
          context.conditions,
          procedureType,
          selectedMethod,
          context.locked,
          isProcedureRunPlan,
          undefined,
          varyingIndicesByComponent[componentName],
          context.showArchivedConditions,
          visibleConditionIndices,
          removedNodeIds
        );
        return table ? [entry(`${selectedMethod.id}_${componentName}`, table)] : [];
      })
    );
  }, [
    context.conditions,
    context.locked,
    context.showArchivedConditions,
    experimentalInputs,
    isProcedureRunPlan,
    procedureType,
    removedNodeIds,
    selectedMethod,
    shouldFilterToVarying,
    templateFlowchart,
    varyingIndicesByComponent,
    visibleConditionIndices,
  ]);

  const filteredSimpleTableDoc = useSimpleEvaluation(filteredFullTableMap);

  const filteredTableViewByComponent = useMemo(() => {
    if (!shouldFilterToVarying) {
      return null;
    }

    return objectFromEntries(
      EXPERIMENTAL_INPUT_COMPONENT_NAMES.map((componentName) =>
        entry(componentName, {
          simpleTableDoc: filteredSimpleTableDoc,
          visibleParameterIndices: varyingIndicesByComponent[componentName],
        })
      )
    ) as Record<ExperimentalInputsComponentName, FilteredTableView>;
  }, [filteredSimpleTableDoc, shouldFilterToVarying, varyingIndicesByComponent]);

  const hasStagedRemovals = removedNodeIds != null && removedNodeIds.size > 0;
  return {
    filteredTableViewByComponent,
    pendingRemovalOptions: hasStagedRemovals
      ? {simpleTableDoc: displaySimpleTableDoc, visibleConditionIndices}
      : null,
    allCount,
    variesCount,
  };
}
